/**
 * CTA occlusion guard.
 *
 * Third-party apps inject fixed-position UI straight into <body> with z-indexes the theme can't
 * sensibly beat (Klaviyo's teaser ships at 90000, chat launchers commonly use 2147483647). On
 * mobile those land on top of purchase CTAs: the Klaviyo "Get 15% Off" teaser covered the cart
 * drawer's Checkout button. Rather than a z-index arms race or a per-vendor selector list, this
 * hit-tests the guarded CTAs and, when an element the theme doesn't own sits on top of one, hides
 * that element until no on-screen CTA is underneath it any more.
 *
 * Never hidden:
 * - Theme-owned UI (sections, <main>, the shared overlay). Stacking there is the theme's own call.
 * - Modal dialogs, and anything covering most of the viewport. Those are deliberate, not floaters.
 * - Consent and cookie banners. The privacy notice must stay reachable.
 * - Shopify's own preview/admin chrome, which only staff ever see.
 *
 * To guard a new CTA, give it the `data-cta-guard` attribute.
 */
(() => {
  const GUARDED_CTAS = [
    '[name="checkout"]',
    '.product-info__add-button [name="add"]',
    '.shopify-payment-button',
    // The whole pinned bar, not just its button: a widget over the bar's edge is one layout shift
    // away from covering the button.
    'sticky-atc-panel',
    '.shelf-configurator__atc-button',
    '[data-cta-guard]'
  ].join(',');

  const THEME_OWNED = '.shopify-section, main, .js-overlay';

  const NEVER_HIDE = [
    '[aria-modal="true"]',
    '[id^="shopify-pc__"]',
    '[id*="consent" i]',
    '[class*="consent" i]',
    '[id*="cookie" i]',
    '[class*="cookie" i]',
    // Shopify's theme-preview bar (68px, bottom-fixed — too small for the viewport-share rule below).
    // It's platform chrome shown only to staff previewing an unpublished theme, so hiding it would
    // just break theme QA. Covers the current "PBar*" ids and the older preview-bar iframe.
    '[id^="PBar"]',
    '[id^="OnlineStorePreviewBar"]',
    '#preview-bar-iframe'
  ].join(',');

  // Containers whose CSS transitions change whether a CTA is on screen.
  const TRANSITION_SOURCES = 'cart-drawer, sticky-atc-panel, quick-add-drawer, .drawer, .modal, .overlay';

  const HIDDEN_ATTR = 'data-cta-guard-hidden';

  // An occluder covering at least this share of the viewport is a modal, not a floating widget.
  const MODAL_VIEWPORT_SHARE = 0.5;

  // Catches widgets that are injected or animate in while a CTA is already on screen.
  const HEARTBEAT_MS = 1000;

  // In-flow CTAs pass under bottom-fixed widgets while scrolling; only act once scrolling stops.
  const SCROLL_SETTLE_MS = 150;

  // Hiding is driven by a stylesheet rule keyed off the attribute rather than by writing the
  // widget's inline style, so the guard and the vendor aren't writing to the same property. The
  // vendor stays free to set its own display (to dismiss itself, say) without the guard either
  // clobbering that on the way out or mistaking it for its own write.
  const sheet = document.createElement('style');
  sheet.textContent = `[${HIDDEN_ATTR}]{display:none!important}`;
  document.head.appendChild(sheet);

  /** @type {Set<Element>} Layers hidden as of the current check. */
  const hidden = new Set();

  /** @type {Map<Element, {display: string, priority: string}>} Inline-style fallback bookkeeping. */
  const inlined = new Map();
  let scrolling = false;
  let scrollTimer = null;
  let frameRequested = false;

  const intersects = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

  /**
   * Whether the CTA is rendered and at least partly inside the viewport.
   * @param {Element} cta - Guarded CTA element.
   * @param {DOMRect} rect - The CTA's already-measured rect (passed in to avoid a second read).
   * @returns {boolean}
   */
  const isOnScreen = (cta, rect) => {
    const viewport = { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
    if (!rect.width || !rect.height || !intersects(rect, viewport)) return false;

    // checkOpacity/checkVisibilityCSS are the pre-Chrome 121 names for the same two options.
    if (cta.checkVisibility) {
      return cta.checkVisibility({
        opacityProperty: true, visibilityProperty: true, checkOpacity: true, checkVisibilityCSS: true
      });
    }

    // iOS Safari only got checkVisibility in 17.4. The opacity half matters here: the sticky ATC
    // panel hides with opacity 0 alongside visibility, and it is itself a guarded CTA.
    const styles = window.getComputedStyle(cta);
    return styles.visibility === 'visible' && styles.opacity !== '0';
  };

  /**
   * Centre plus inset corners: a widget over one end of a button still eats taps.
   * @param {DOMRect} rect - CTA bounding rect.
   * @returns {Array<Array<number>>}
   */
  const samplePoints = (rect) => {
    const inset = Math.min(6, rect.width / 4, rect.height / 4);
    return [
      [rect.left + rect.width / 2, rect.top + rect.height / 2],
      [rect.left + inset, rect.top + inset],
      [rect.right - inset, rect.top + inset],
      [rect.left + inset, rect.bottom - inset],
      [rect.right - inset, rect.bottom - inset]
    ];
  };

  /**
   * Finds the third-party floating layer covering the CTA at a viewport point, if there is one.
   * @param {Element} cta - Guarded CTA element.
   * @param {number} x - Viewport x coordinate.
   * @param {number} y - Viewport y coordinate.
   * @param {Map} resolved - Per-check memo of hit element to resolved layer.
   * @returns {?Element}
   */
  const occluderAt = (cta, x, y, resolved) => {
    const hit = document.elementFromPoint(x, y);
    if (!hit || cta.contains(hit) || hit.closest(THEME_OWNED) || hit.closest(NEVER_HIDE)) return null;

    // The sample points of one CTA usually land on the same node, and the walk below is the
    // expensive part, so resolve each hit element only once per check.
    if (resolved.has(hit)) return resolved.get(hit);

    // Hide the whole floating layer, not just the inner node that happened to be hit.
    let layer = hit;
    while (layer && !['fixed', 'sticky'].includes(window.getComputedStyle(layer).position)) {
      layer = layer.parentElement;
    }
    if (layer && layer.querySelector(NEVER_HIDE)) layer = null;

    if (layer) {
      const rect = layer.getBoundingClientRect();
      const viewportArea = window.innerWidth * window.innerHeight;
      if (rect.width * rect.height >= viewportArea * MODAL_VIEWPORT_SHARE) layer = null;
    }

    resolved.set(hit, layer);
    return layer;
  };

  /**
   * Hides a layer.
   * @param {Element} layer - Third-party floating layer.
   */
  const hide = (layer) => {
    hidden.add(layer);
    layer.setAttribute(HIDDEN_ATTR, '');

    // The stylesheet rule above loses to an inline `display: ... !important`, which some widgets
    // set to beat host-page CSS. Only then fall back to writing the inline style ourselves.
    if (window.getComputedStyle(layer).display !== 'none') {
      if (!inlined.has(layer)) {
        inlined.set(layer, {
          display: layer.style.getPropertyValue('display'),
          priority: layer.style.getPropertyPriority('display')
        });
      }
      layer.style.setProperty('display', 'none', 'important');
    }
  };

  /**
   * Stops hiding a layer, putting back any inline style the fallback above overwrote.
   * @param {Element} layer - Previously hidden layer.
   */
  const release = (layer) => {
    layer.removeAttribute(HIDDEN_ATTR);

    const saved = inlined.get(layer);
    if (!saved) return;
    inlined.delete(layer);

    // Only undo the fallback write if it's still ours — if the widget has since set its own
    // display, that newer value wins.
    const style = layer.style;
    if (style.getPropertyValue('display') !== 'none' || style.getPropertyPriority('display') !== 'important') return;

    if (saved.display) {
      style.setProperty('display', saved.display, saved.priority);
    } else {
      style.removeProperty('display');
    }
  };

  const check = () => {
    frameRequested = false;
    if (document.hidden || scrolling) return;

    // Stop hiding everything first, then work out from live geometry what still needs hiding. A
    // hidden element has no box to measure, so the alternative is judging it by the rect it had
    // when it was hidden — and a widget that repositions itself while hidden (or whose CTA never
    // moves, like the pinned ATC bar) would then never be let go. This whole pass is synchronous
    // inside one frame, so the released state is never painted.
    hidden.forEach(release);
    hidden.clear();

    const ctas = [];
    const ctaRects = [];
    document.querySelectorAll(GUARDED_CTAS).forEach((cta) => {
      const rect = cta.getBoundingClientRect();
      if (isOnScreen(cta, rect)) {
        ctas.push(cta);
        ctaRects.push(rect);
      }
    });

    // Every measurement happens before the first hide(), so writing to one layer can't force the
    // next elementFromPoint to re-run layout.
    const resolved = new Map();
    const occluders = new Set();
    ctas.forEach((cta, index) => {
      samplePoints(ctaRects[index]).forEach(([x, y]) => {
        const layer = occluderAt(cta, x, y, resolved);
        if (layer) occluders.add(layer);
      });
    });

    occluders.forEach(hide);
  };

  const scheduleCheck = () => {
    if (frameRequested) return;
    frameRequested = true;
    requestAnimationFrame(check);
  };

  window.addEventListener('scroll', () => {
    scrolling = true;
    clearTimeout(scrollTimer);
    scrollTimer = setTimeout(() => {
      scrolling = false;
      scheduleCheck();
    }, SCROLL_SETTLE_MS);
  }, { passive: true });

  // Drawers, modals and the sticky ATC panel all finish opening/closing with a CSS transition, and
  // that's the moment a CTA appears or leaves. Scoped to those containers because the theme also
  // transitions nav links, product cards and buttons on hover — firing a full check on every one of
  // those would be pure waste. Anything this misses is picked up by the heartbeat.
  document.addEventListener('transitionend', (evt) => {
    if (evt.target.closest && evt.target.closest(TRANSITION_SOURCES)) scheduleCheck();
  });
  document.addEventListener('visibilitychange', scheduleCheck);
  window.addEventListener('on:debounced-resize', scheduleCheck);
  window.addEventListener('pageshow', scheduleCheck);
  setInterval(scheduleCheck, HEARTBEAT_MS);
  scheduleCheck();
})();
